import { Badge } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import type { BodyRowConfig } from '@ztechapps/webui.data-tools'
import type { EntityWithSelection } from '@ztechapps/common.hook'
import type { Chat } from '@ztechapps/common.contract'
import { CheckboxBodyCell, RowNumberBodyCell } from '@ztechapps/webui.data-tools'
import { LocalDateTime } from '@ztechapps/webui.date'
import { Tags } from '@ztechapps/webui.input'
import { HorizontalStack, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { AssignToMeLink } from '../Conversation/AssignToMeLink'
import { ChatStatusBadge } from '../Conversation/ChatStatusBadge'
import { useConversationInboxContext } from './Context'

export const InboxListRow = (props: BodyRowConfig<EntityWithSelection<Chat>>) => {
  const {
    _id,
    _selected,
    code,
    externalId,
    subject,
    sender,
    status,
    document,
    metadata,
    assignee,
    messageCount,
    unreadForAgentCount,
    lastMessageAt,
    createdAt,
    tags,
    remarks,
  } = props
  const { chatListWithSelection, renderDocumentCell, conversationItemRoute } = useConversationInboxContext()
  const { toggleSelectBy } = chatListWithSelection
  // No link target when neither key exists — a Link to /undefined must never render.
  const conversationKey = externalId ?? _id
  const hasUnread = (unreadForAgentCount ?? 0) > 0

  return (
    <>
      <RowNumberBodyCell {...props} />
      <CheckboxBodyCell checked={_selected} onChange={() => toggleSelectBy((item) => item._id === _id)} />
      {props._renderDataBodyCell({
        code: conversationKey ? (
          <Link to={conversationItemRoute.altPath({ _id: conversationKey })} className="text-nowrap">
            <b>{code}</b>
          </Link>
        ) : (
          <b>{code}</b>
        ),
        // The thread length rides beside the subject as a count badge — always rendered, zero
        // included — instead of its own column, which the generic list query could neither sort
        // nor narrow.
        // An unread customer message leads the subject with a dot (the `p-1` span is Bootstrap's
        // own dot idiom) and bolds it; the dot carries the accessible label, the cell's text
        // stays the subject alone. The dot's hint reads from `TooltipPopOver`, never a native `title`
        // (`components/tooltips.md` → "Rules"); a dense list row raises `delay.show`.
        subject: (
          <HorizontalStack gap={1}>
            <ShowIf condition={hasUnread}>
              <TooltipPopOver tooltipBody="Unread customer message" delay={{ show: 1000, hide: 0 }}>
                <span
                  role="img"
                  aria-label="Unread customer message"
                  className="d-inline-block flex-shrink-0 p-1 rounded-circle bg-primary"
                />
              </TooltipPopOver>
            </ShowIf>
            <span className={hasUnread ? 'text-break fw-bold' : 'text-break'}>{subject}</span>
            <Badge bg="danger" className="fw-normal">
              {messageCount ?? 0}
            </Badge>
          </HorizontalStack>
        ),
        requester: sender?.profile?.name || sender?.profile?.email,
        company: sender?.company,
        status: <ChatStatusBadge status={status} />,
        document: renderDocumentCell({ document, metadata }),
        assignee: assignee?.profile?.name || <AssignToMeLink chatId={_id} />,
        lastMessageAt: <LocalDateTime value={lastMessageAt} />,
        createdAt: <LocalDateTime value={createdAt} />,
        // Filter-only column: shown through Table Config it reads as its header and filter choices do.
        unreadForAgentCount: hasUnread ? 'Unread' : 'Read',
        tags: <Tags dataArray={tags} />,
        remarks: <span className="text-truncate">{remarks}</span>,
      })}
    </>
  )
}
